import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ListChecks, FolderKanban, LogOut, ChevronDown, Building2, MapPin, Settings, ShieldAlert, Check, Layers, ChevronsLeft, ChevronsRight, UserCircle, CalendarDays, Search, X, Megaphone, RefreshCw, KeyRound, Award, HandCoins, Palette, Clock } from "lucide-react";
import BrowserCompatBanner from "@/components/BrowserCompatBanner";
import HealthCheckWidget from "@/components/HealthCheckWidget";
import ForceChangePasswordDialog from "@/components/ForceChangePasswordDialog";
import { useAuth } from "@/lib/auth";
import { useProjectFilter } from "@/lib/projectFilter";
import { useAccess } from "@/lib/access";
import { LOGO_URL, DIVISION_COLOR, fmtDate, projectDisplay } from "@/lib/constants";
import ProfileDialog from "@/components/ProfileDialog";
import DisplaySettingsDialog from "@/components/DisplaySettingsDialog";
import { loadDisplayPrefs, applyTheme, isTimeInWindow, readDisplayPrefsCache } from "@/lib/displayPrefs";
import { NAV_GROUPS, loadNavOrder, readNavOrderCache, resolveNavOrder, orderNavGroups } from "@/lib/navOrder";
import AnnouncePopup from "@/components/AnnouncePopup";
import NotificationBell from "@/components/NotificationBell";
import MessengerFAB from "@/components/messenger/MessengerFAB";
import api from "@/lib/api";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

/**
 * LiveClock — jam real-time (HH:MM:SS + offset GMT) di topbar, tepat di
 * kiri indikator "SERVER OK". 2026-06-11 (user request). Waktu mengikuti
 * zona perangkat user; offset dihitung dari getTimezoneOffset.
 */
function LiveClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const iv = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);
  const hh = String(now.getHours()).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  const ss = String(now.getSeconds()).padStart(2, "0");
  const offMin = -now.getTimezoneOffset(); // WIB = +420
  const sign = offMin >= 0 ? "+" : "-";
  const abs = Math.abs(offMin);
  const gmt = `GMT${sign}${Math.floor(abs / 60)}${abs % 60 ? `:${String(abs % 60).padStart(2, "0")}` : ""}`;
  return (
    <div data-testid="topbar-live-clock"
      className="hidden md:flex items-center gap-1.5 px-2.5 h-9 border border-slate-200 text-slate-600"
      title="Waktu lokal perangkat Anda">
      <Clock size={12} className="text-slate-400" />
      <span className="font-mono text-xs tabular-nums font-semibold text-[#0B1D3A]">{hh}:{mm}:{ss}</span>
      <span className="text-[9px] uppercase tracking-wider text-slate-400">{gmt}</span>
    </div>
  );
}

/**
 * Global refresh button in the top header. Fetches fresh data from the server
 * without dropping the current route by dispatching a `ens:refresh` event that
 * every page can listen to; also does a soft window.location.reload() fallback
 * for pages that haven't wired the event yet. Rotates icon for 600ms as feedback.
 */
function RefreshButton() {
  const [spinning, setSpinning] = useState(false);
  const onClick = () => {
    if (spinning) return;
    setSpinning(true);
    try { window.dispatchEvent(new Event("ens:refresh")); } catch {}
    // Give listeners ~400ms to refetch; if they don't consume, do a full reload
    // to guarantee the user always sees the freshest data.
    setTimeout(() => {
      setSpinning(false);
      try { window.location.reload(); } catch {}
    }, 550);
  };
  return (
    <button
      type="button"
      data-testid="btn-global-refresh"
      onClick={onClick}
      title="Refresh — muat ulang data terbaru"
      aria-label="Refresh halaman"
      className="flex items-center justify-center w-9 h-9 border border-slate-200 hover:bg-slate-50 text-slate-600 hover:text-[#FF5A1F] transition-colors"
    >
      <RefreshCw size={15} className={spinning ? "animate-spin" : ""} />
    </button>
  );
}

export default function AppShell() {
  const { user, logout, mustChangePassword } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  // 2026-06-11: + /my-work — Kanban Task (yang memakai project filter) kini
  // hidup di dalam halaman My Work & Tasks setelah merge.
  const showProjectSelector = location.pathname === "/my-work" || location.pathname === "/tasks" || location.pathname === "/dashboard";
  const { projects, selected, isAll, selectedIds, toggleId, clearAll, selectAllActive, reload } = useProjectFilter();
  const [openProfile, setOpenProfile] = useState(false);
  // 2026-06-11 (DISPLAY-PREFS): preferensi tampilan per-user (tema + sidebar).
  // Load instan dari localStorage lalu sync dari server.
  const [openDisplay, setOpenDisplay] = useState(false);
  const [displayPrefs, setDisplayPrefs] = useState(() => readDisplayPrefsCache());
  const [scheduleOverride, setScheduleOverride] = useState(null);
  useEffect(() => {
    let alive = true;
    loadDisplayPrefs().then((p) => { if (alive) setDisplayPrefs(p); });
    return () => { alive = false; };
  }, []);
  // 2026-06-13 (NAV-ORDER): urutan grup menu sidebar — personal > default
  // global (diatur super admin di Settings → Urutan Menu) > bawaan kode.
  // Event "ens:nav-order-changed" dipicu setelah simpan di Settings supaya
  // sidebar langsung re-order tanpa refresh halaman.
  const [navOrderRes, setNavOrderRes] = useState(() => readNavOrderCache());
  useEffect(() => {
    let alive = true;
    const reload = () => { loadNavOrder().then((r) => { if (alive) setNavOrderRes(r); }); };
    reload();
    window.addEventListener("ens:nav-order-changed", reload);
    return () => { alive = false; window.removeEventListener("ens:nav-order-changed", reload); };
  }, []);
  // Terapkan tema + re-eval tiap 60 detik (transisi jadwal otomatis).
  useEffect(() => {
    applyTheme(displayPrefs);
    const iv = setInterval(() => applyTheme(displayPrefs), 60000);
    return () => clearInterval(iv);
  }, [displayPrefs]);
  // Jadwal sidebar: hapus override tombol saat jendela jadwal berganti.
  const lastWindowRef = useRef(null);
  useEffect(() => {
    if (displayPrefs.sidebar?.mode !== "schedule") return undefined;
    const check = () => {
      const inWin = isTimeInWindow(new Date(), displayPrefs.sidebar.openStart, displayPrefs.sidebar.openEnd);
      if (lastWindowRef.current !== null && lastWindowRef.current !== inWin) setScheduleOverride(null);
      lastWindowRef.current = inWin;
    };
    check();
    const iv = setInterval(check, 60000);
    return () => clearInterval(iv);
  }, [displayPrefs]);
  // "Pinned" collapse — user's explicit choice via toggle button. Persists in localStorage.
  // DEFAULT: auto-collapse (true) supaya sidebar tertutup otomatis; hanya expand saat hover.
  // User bisa pin-expand via tombol untuk override. Backward compat: "0" = expanded pinned.
  const [pinnedCollapsed, setPinnedCollapsed] = useState(() => {
    const v = localStorage.getItem("ens_sidebar_collapsed");
    return v === null ? true : v === "1"; // null (never set) → default auto-hide
  });
  // Ephemeral hover state — when the user's cursor is over the sidebar, it auto-expands
  // regardless of pinned state; when they leave, it collapses back with a small delay
  // (300ms) supaya tidak flicker saat gerakan mouse cepat.
  const [hovering, setHovering] = useState(false);
  const hoverEnterTimerRef = useRef(null);
  const hoverLeaveTimerRef = useRef(null);
  // R3.2C.1: ref topbar untuk ResizeObserver → update --ens-topbar-height responsif
  const topbarRef = useRef(null);
  useEffect(() => {
    const el = topbarRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const applyHeight = () => {
      const h = el.getBoundingClientRect().height;
      if (h && Number.isFinite(h)) {
        // Set actual measured topbar height as CSS var.
        // --ens-table-sticky-top adalah calc() dari --ens-topbar-height +
        // --ens-context-height, sehingga sticky offset otomatis follow.
        document.documentElement.style.setProperty("--ens-topbar-height", `${Math.round(h)}px`);
      }
    };
    applyHeight();
    const ro = new ResizeObserver(applyHeight);
    ro.observe(el);
    window.addEventListener("resize", applyHeight);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", applyHeight);
    };
  }, []);
  const cancelHoverTimers = () => {
    if (hoverEnterTimerRef.current) { clearTimeout(hoverEnterTimerRef.current); hoverEnterTimerRef.current = null; }
    if (hoverLeaveTimerRef.current) { clearTimeout(hoverLeaveTimerRef.current); hoverLeaveTimerRef.current = null; }
  };
  const onSidebarEnter = () => {
    cancelHoverTimers();
    hoverEnterTimerRef.current = setTimeout(() => setHovering(true), 150);
  };
  const onSidebarLeave = () => {
    cancelHoverTimers();
    // 2026-06-19 (user request): auto-hide bisa dimatikan (sidebar tetap terbuka
    // setelah hover) dan durasi jeda sebelum hide bisa diatur per-user.
    const sb = displayPrefs?.sidebar || {};
    if (sb.autoHide === false) return;
    const delayMs = Math.max(0, Number(sb.hideDelaySec ?? 0.3)) * 1000;
    hoverLeaveTimerRef.current = setTimeout(() => setHovering(false), delayMs);
  };
  // 2026-06-11 (DISPLAY-PREFS): mode sidebar per-user.
  // - auto     : tertutup default + expand saat hover (perilaku lama)
  // - manual   : buka/tutup hanya via tombol — tanpa auto-hover expand
  // - schedule : terbuka pada rentang jam yg diatur user; di luar itu tertutup
  //              (hover tetap expand; tombol jadi override sampai jadwal flip)
  const sidebarMode = displayPrefs?.sidebar?.mode || "auto";
  const scheduleExpanded = sidebarMode === "schedule"
    ? isTimeInWindow(new Date(), displayPrefs.sidebar.openStart, displayPrefs.sidebar.openEnd)
    : null;
  const effectiveCollapsed = sidebarMode === "schedule"
    ? (scheduleOverride !== null ? scheduleOverride : !scheduleExpanded)
    : pinnedCollapsed;
  const allowHoverExpand = sidebarMode !== "manual";
  const collapsed = effectiveCollapsed && !(allowHoverExpand && hovering);
  const [projectSearch, setProjectSearch] = useState("");
  const [projDropdownOpen, setProjDropdownOpen] = useState(false);

  // Cmd/Ctrl+K shortcut → open project dropdown & auto-focus search
  useEffect(() => {
    if (!showProjectSelector) return;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setProjDropdownOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showProjectSelector]);

  const toggleCollapsed = () => {
    // Saat user menutup manual, hentikan state hover supaya auto-hide-off tidak
    // menahan sidebar tetap terbuka.
    cancelHoverTimers();
    setHovering(false);
    if (sidebarMode === "schedule") {
      // Override sesi — berlaku sampai jendela jadwal berganti (lihat effect di atas).
      setScheduleOverride((o) => { const cur = o !== null ? o : !scheduleExpanded; return !cur; });
      return;
    }
    setPinnedCollapsed((c) => {
      const next = !c;
      localStorage.setItem("ens_sidebar_collapsed", next ? "1" : "0");
      return next;
    });
  };

  const { canAccess, error: accessError, reload: reloadAccess, ready: accessReady } = useAccess();

  // 149-au: My Work badge — jumlah item "for_me" yang menunggu tindakan user.
  const [myWorkBadge, setMyWorkBadge] = useState(0);
  useEffect(() => {
    let alive = true;
    const load = () => {
      api.get("/my-work/counts").then(({ data }) => {
        if (alive) setMyWorkBadge(Number(data?._badge) || 0);
      }).catch(() => { /* silent */ });
    };
    load();
    const interval = setInterval(load, 60000); // refresh setiap 60 detik
    const onRefresh = () => load();
    window.addEventListener("ens:refresh", onRefresh);
    return () => { alive = false; clearInterval(interval); window.removeEventListener("ens:refresh", onRefresh); };
  }, []);

  const groups = orderNavGroups(NAV_GROUPS, resolveNavOrder(navOrderRes))
    .map((g) => ({
      ...g,
      items: g.items.filter((n) => {
        if (n.superAdminOnly && !(user?.division === "BOD" && user?.level === 1)) return false;
        if (n.bodOnly && user?.division !== "BOD") return false;
        // 149-au: badgeSource items (mis. My Work) selalu visible — bukan feature-gated.
        // R232: `features` (array) = tampil bila SALAH SATU fitur boleh diakses
        // (mis. Kehadiran & Perizinan untuk pemegang akses attendance/leaves).
        const _feats = n.features || (n.feature ? [n.feature] : null);
        if (_feats && !_feats.some((f) => canAccess(user, f))) return false;
        return true;
      }),
    }))
    .filter((g) => g.items.length > 0);

  // 149-cc: Accordion sidebar. Per-user open state persisted di localStorage.
  // Grup dgn active route otomatis dibuka pada saat mount / route change.
  const navStorageKey = user?.id ? `ens_nav_open_${user.id}` : "ens_nav_open_anon";
  const [openGroups, setOpenGroups] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(navStorageKey) || "[]")); }
    catch { return new Set(); }
  });
  // Auto-open grup yg mengandung route aktif
  useEffect(() => {
    const activeGroup = groups.find((g) => !g.flat && g.items.some((n) => location.pathname === n.to || location.pathname.startsWith(n.to + "/")));
    if (activeGroup && !openGroups.has(activeGroup.key)) {
      setOpenGroups((prev) => {
        const next = new Set(prev); next.add(activeGroup.key);
        try { localStorage.setItem(navStorageKey, JSON.stringify([...next])); } catch {}
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, navStorageKey]);
  const toggleGroup = (key) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      try { localStorage.setItem(navStorageKey, JSON.stringify([...next])); } catch {}
      return next;
    });
  };

  const current = projectDisplay(selected);
  const showSearch = projects.length > 5;
  const q = projectSearch.trim().toLowerCase();
  const matchesQ = useCallback((p) => {
    if (!q) return true;
    const hay = [
      p.project_name, p.client, p.variation_order_name,
      p.wo_number, p.parent_project_name, p.internal_key,
    ].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q);
  }, [q]);
  const internalProjects = useMemo(() => projects.filter((p) => p.project_type === "internal").filter(matchesQ), [projects, matchesQ]);
  const newProjects = useMemo(() => projects.filter((p) => p.project_type === "new_project").filter(matchesQ), [projects, matchesQ]);
  const voProjects = useMemo(() => projects.filter((p) => p.project_type === "variation_order").filter(matchesQ), [projects, matchesQ]);
  const totalMatches = internalProjects.length + newProjects.length + voProjects.length;

  return (
    <div className="min-h-screen flex bg-[#FAFAFA]">
      <aside
        className={`${collapsed ? "w-16" : "w-64"} bg-[#0B1D3A] text-white flex flex-col fixed inset-y-0 left-0 z-50 transition-[width] duration-200`}
        data-testid="app-sidebar"
        data-collapsed={collapsed ? "1" : "0"}
        data-pinned-collapsed={pinnedCollapsed ? "1" : "0"}
        onMouseEnter={onSidebarEnter}
        onMouseLeave={onSidebarLeave}
      >
        <div className={`${collapsed ? "px-3 py-4" : "p-5"} border-b border-white/10 flex items-center justify-between gap-2`}>
          {!collapsed && (
            <div className="min-w-0">
              <div className="bg-white p-2 inline-flex">
                <img src={LOGO_URL} alt="ENS" className="h-8" />
              </div>
              <p className="overline text-white/50 mt-3">ENERGI HUB</p>
            </div>
          )}
          <button
            type="button"
            data-testid="btn-toggle-sidebar"
            onClick={toggleCollapsed}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="text-white/60 hover:text-white hover:bg-white/10 p-1.5 shrink-0"
          >
            {collapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
          </button>
        </div>
        <nav className="flex-1 py-4 overflow-y-auto overflow-x-hidden" data-testid="sidebar-nav">
          {groups.map((g) => {
            const isOpen = g.flat || collapsed || openGroups.has(g.key);
            const hasActive = g.items.some((n) => location.pathname === n.to || location.pathname.startsWith(n.to + "/"));
            const groupTestid = `nav-group-${g.key || g.label.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`;
            return (
            <div key={g.label} className="mb-2" data-testid={groupTestid} data-open={isOpen ? "1" : "0"}>
              {!collapsed && !g.flat && (
                <button
                  type="button"
                  data-testid={`btn-toggle-${g.key}`}
                  onClick={() => toggleGroup(g.key)}
                  aria-expanded={isOpen}
                  aria-controls={`nav-panel-${g.key}`}
                  aria-label={`${isOpen ? "Tutup" : "Buka"} grup ${g.label}`}
                  className={`w-full flex items-center justify-between gap-2 px-5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] hover:text-white transition-colors ${hasActive ? "text-white" : "text-white/40"}`}
                >
                  <span>{g.label}</span>
                  <ChevronDown size={12} className={`transition-transform duration-150 ${isOpen ? "rotate-0" : "-rotate-90"}`} aria-hidden="true" />
                </button>
              )}
              {!collapsed && g.flat && (
                <div className="px-5 pb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/40" aria-hidden="true">{g.label}</div>
              )}
              <div
                id={`nav-panel-${g.key}`}
                role="region"
                aria-labelledby={`btn-toggle-${g.key}`}
                className={`space-y-0.5 overflow-hidden transition-[max-height] duration-150 ease-out ${isOpen ? "max-h-96" : "max-h-0"}`}
              >
                {g.items.map((n) => {
                  const badgeCount = n.badgeSource === "my_work" ? myWorkBadge : 0;
                  return (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    data-testid={n.testid}
                    title={collapsed ? n.label : undefined}
                    aria-label={n.label}
                    className={({ isActive }) => `side-link ${isActive ? "active" : ""} ${collapsed ? "justify-center px-0" : ""} relative`}
                  >
                    <n.icon size={16} strokeWidth={2} aria-hidden="true" />
                    {!collapsed && <span className="flex-1">{n.label}</span>}
                    {badgeCount > 0 && !collapsed && (
                      <span
                        data-testid={`nav-badge-${n.testid}`}
                        className="ml-auto px-1.5 py-0.5 text-[10px] font-bold tabular-nums bg-[#FF5A1F] text-white"
                        title={`${badgeCount} pekerjaan menunggu`}
                      >{badgeCount}</span>
                    )}
                    {badgeCount > 0 && collapsed && (
                      <span
                        data-testid={`nav-badge-nav-${n.testid.replace(/^nav-/, "")}`}
                        className="absolute top-1 right-1 h-2 w-2 bg-[#FF5A1F]"
                        title={`${badgeCount} pekerjaan menunggu`}
                        data-count={badgeCount}
                      />
                    )}
                  </NavLink>
                  );
                })}
              </div>
            </div>
            );
          })}
        </nav>
        {!collapsed ? (
          <div className="p-4 border-t border-white/10 text-xs text-white/50">
            <div className="font-semibold text-white/80 truncate">{user?.name}</div>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${DIVISION_COLOR[user?.division] || "bg-slate-600"}`}>{user?.division}</span>
              <span className="px-1.5 py-0.5 text-[10px] bg-white/10 uppercase tracking-wider">Lv{user?.level}</span>
            </div>
            <div className="mt-1 text-white/40">{user?.sub_division}</div>
          </div>
        ) : (
          <div className="p-2 border-t border-white/10 flex justify-center" title={`${user?.name} · ${user?.division} · Lv${user?.level}`}>
            <div className={`w-8 h-8 flex items-center justify-center text-[10px] font-bold uppercase ${DIVISION_COLOR[user?.division] || "bg-slate-600"}`}>
              {user?.name?.[0] || "?"}
            </div>
          </div>
        )}
      </aside>

      <div className={`${collapsed ? "ml-16" : "ml-64"} flex-1 flex flex-col ens-clip-x transition-[margin] duration-200`}>
        <BrowserCompatBanner />
        {/* 149-cw-r152: Banner jika access matrix gagal dimuat — root cause
            "sidebar tiba-tiba kosong". Config kosong = canAccess semua return
            false = sidebar filter out semua item feature-gated. */}
        {accessReady && accessError && (
          <div
            data-testid="access-load-error-banner"
            role="alert"
            className="bg-amber-50 border-b border-amber-300 px-4 py-2 text-xs text-amber-900 flex items-center justify-between gap-3 sticky top-0 z-30"
          >
            <div className="flex items-center gap-2 min-w-0">
              <ShieldAlert size={14} className="text-amber-700 shrink-0" />
              <span className="truncate">
                <b>Akses fitur belum termuat.</b> Sidebar/menu mungkin tampak kosong. Klik <b>Muat Ulang</b> — atau refresh halaman kalau masalah tetap.
              </span>
            </div>
            <button
              type="button"
              onClick={() => reloadAccess()}
              data-testid="btn-reload-access"
              className="shrink-0 px-2 py-1 border border-amber-500 bg-white text-amber-900 hover:bg-amber-100 text-[10px] uppercase tracking-wider font-semibold inline-flex items-center gap-1"
            >
              <RefreshCw size={11} /> Muat Ulang
            </button>
          </div>
        )}
        <header ref={topbarRef} data-testid="app-topbar" className="h-16 bg-white border-b border-slate-200 sticky top-0 z-20 flex items-center justify-between px-6">
          <div className="flex items-center gap-4 min-w-0">
            {showProjectSelector ? (
              <>
                <Building2 size={18} className="text-[#FF5A1F] shrink-0" />
                <div className="min-w-0">
                  <p className="overline text-slate-500 leading-none">Proyek Aktif</p>

                  {/* Multi-select project filter (only visible on /tasks) */}
                  <DropdownMenu open={projDropdownOpen} onOpenChange={setProjDropdownOpen} modal={false}>
                    <DropdownMenuTrigger asChild>
                      <button
                        data-testid="btn-project-selector"
                        title="Cmd/Ctrl + K"
                        className="mt-1 flex items-center gap-2 min-w-0 hover:bg-slate-50 -mx-2 px-2 py-1 transition-colors group"
                      >
                        {isAll ? (
                          <>
                            <Layers size={14} className="text-slate-500" />
                            <span data-testid="active-project-name" className="font-display font-semibold text-[#0B1D3A] text-base">Semua Proyek</span>
                          </>
                        ) : selectedIds.length === 1 && current ? (
                          <>
                            <span data-testid="active-project-name" className="font-display font-semibold text-[#0B1D3A] truncate text-base">{current.title}</span>
                            {current.isVO && <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider bg-[#FF5A1F] text-white">VO</span>}
                            {selected?.project_type === "internal" && <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider bg-slate-700 text-white">Internal</span>}
                            {current.subtitle && <span className="text-xs text-slate-600 hidden md:inline">· {current.subtitle}</span>}
                            {current.client && <span className="text-xs text-slate-500 hidden md:inline">· {current.client}</span>}
                          </>
                        ) : (
                          <>
                            <FolderKanban size={14} className="text-[#FF5A1F]" />
                            <span data-testid="active-project-name" className="font-display font-semibold text-[#0B1D3A] text-base tabular-nums">
                              {selectedIds.length} projects
                            </span>
                          </>
                        )}
                        <ChevronDown size={14} className="text-slate-400 group-hover:text-[#0B1D3A]" />
                        <span className="hidden lg:inline-flex items-center gap-0.5 ml-1 text-[9px] font-mono border border-slate-300 text-slate-500 px-1 py-0.5 tracking-wider" data-testid="proj-shortcut-hint">
                          ⌘K
                        </span>
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="start"
                      sideOffset={6}
                      collisionPadding={16}
                      onCloseAutoFocus={(e) => { e.preventDefault(); setProjectSearch(""); }}
                      className="rounded-none w-96 max-h-[70vh] overflow-y-auto"
                    >
                      <DropdownMenuLabel className="text-xs overline text-slate-500">
                        Pilih project · <span className="text-[10px] normal-case text-slate-400 tracking-normal">bisa lebih dari satu</span>
                      </DropdownMenuLabel>
                      {showSearch && (
                        <div className="px-2 pb-2 sticky top-0 bg-white z-10" data-testid="project-search-wrap">
                          <div className="relative">
                            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                            <input
                              type="text"
                              autoFocus
                              value={projectSearch}
                              onChange={(e) => setProjectSearch(e.target.value)}
                              onKeyDown={(e) => { e.stopPropagation(); }}
                              placeholder={`Cari di ${projects.length} project...`}
                              data-testid="project-search-input"
                              className="w-full pl-7 pr-7 py-1.5 text-xs border border-slate-200 focus:outline-none focus:border-[#FF5A1F] tabular-nums"
                            />
                            {projectSearch && (
                              <button
                                type="button"
                                data-testid="project-search-clear"
                                onMouseDown={(e) => { e.preventDefault(); setProjectSearch(""); }}
                                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-rose-600 p-0.5"
                              >
                                <X size={11} />
                              </button>
                            )}
                          </div>
                          {q && (
                            <div className="mt-1 text-[10px] text-slate-500 tabular-nums" data-testid="project-search-count">
                              {totalMatches} dari {projects.length} project cocok
                            </div>
                          )}
                        </div>
                      )}
                      <DropdownMenuItem
                        data-testid="proj-select-all"
                        onSelect={(e) => e.preventDefault()}
                        onClick={clearAll}
                        className="text-sm"
                      >
                        <Layers size={14} className="mr-2" /> Semua Proyek (hapus filter)
                        {isAll && <Check size={14} className="ml-auto text-[#FF5A1F]" />}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        data-testid="proj-select-all-active"
                        onSelect={(e) => e.preventDefault()}
                        onClick={selectAllActive}
                        className="text-sm text-[#FF5A1F] font-semibold"
                      >
                        <Check size={14} className="mr-2" /> Select All Active ({projects.length})
                      </DropdownMenuItem>

                      {showSearch && q && totalMatches === 0 && (
                        <div className="px-3 py-4 text-center text-xs text-slate-400" data-testid="project-search-empty">
                          Tidak ada project cocok dengan "{projectSearch}".
                        </div>
                      )}

                      {internalProjects.length > 0 && <>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel className="text-[10px] overline text-slate-400">Internal</DropdownMenuLabel>
                        {internalProjects.map((p) => (
                          <DropdownMenuItem
                            key={p.id}
                            data-testid={`proj-select-${p.internal_key}`}
                            onSelect={(e) => e.preventDefault()}
                            onClick={() => toggleId(p.id)}
                            className="text-sm"
                          >
                            <span className={`mr-2 w-4 h-4 border ${selectedIds.includes(p.id) ? "bg-[#FF5A1F] border-[#FF5A1F]" : "border-slate-300"} inline-flex items-center justify-center shrink-0`}>
                              {selectedIds.includes(p.id) && <Check size={11} className="text-white" />}
                            </span>
                            <Building2 size={13} className="mr-2 text-slate-500" /> {p.project_name}
                          </DropdownMenuItem>
                        ))}
                      </>}

                      {newProjects.length > 0 && <>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel className="text-[10px] overline text-slate-400">Projects</DropdownMenuLabel>
                        {newProjects.map((p) => (
                          <DropdownMenuItem
                            key={p.id}
                            data-testid={`proj-select-${p.id}`}
                            onSelect={(e) => e.preventDefault()}
                            onClick={() => toggleId(p.id)}
                            className="text-sm"
                          >
                            <span className={`mr-2 w-4 h-4 border ${selectedIds.includes(p.id) ? "bg-[#FF5A1F] border-[#FF5A1F]" : "border-slate-300"} inline-flex items-center justify-center shrink-0`}>
                              {selectedIds.includes(p.id) && <Check size={11} className="text-white" />}
                            </span>
                            <FolderKanban size={13} className="mr-2 text-slate-500" />
                            <div className="min-w-0 flex-1">
                              <div className="truncate">{p.project_name}</div>
                              {p.client && <div className="text-[10px] text-slate-500 truncate">{p.client}</div>}
                            </div>
                          </DropdownMenuItem>
                        ))}
                      </>}

                      {voProjects.length > 0 && <>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel className="text-[10px] overline text-slate-400">Variation Orders</DropdownMenuLabel>
                        {voProjects.map((p) => (
                          <DropdownMenuItem
                            key={p.id}
                            data-testid={`proj-select-vo-${p.id}`}
                            onSelect={(e) => e.preventDefault()}
                            onClick={() => toggleId(p.id)}
                            className="text-sm"
                          >
                            <span className={`mr-2 w-4 h-4 border ${selectedIds.includes(p.id) ? "bg-[#FF5A1F] border-[#FF5A1F]" : "border-slate-300"} inline-flex items-center justify-center shrink-0`}>
                              {selectedIds.includes(p.id) && <Check size={11} className="text-white" />}
                            </span>
                            <span className="px-1 py-0.5 text-[8px] font-semibold uppercase tracking-wider bg-[#FF5A1F] text-white mr-2">VO</span>
                            <div className="min-w-0 flex-1">
                              <div className="truncate">{p.variation_order_name}</div>
                              <div className="text-[10px] text-slate-500 truncate">← {p.parent_project_name}</div>
                            </div>
                          </DropdownMenuItem>
                        ))}
                      </>}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            {/* 2026-06-11 (user request): jam real-time di kiri indikator server. */}
            <LiveClock />
            {/* 149-cw-r154: HealthCheckWidget — indikator koneksi (poll /api/health 30s). */}
            <HealthCheckWidget />
            <RefreshButton />
            <NotificationBell />
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="flex items-center gap-2 px-3 h-9 border border-slate-200 hover:bg-slate-50 text-sm">
                  <span className="hidden sm:inline text-slate-700 max-w-[160px] truncate">{user?.name.split(" ")[0]}</span>
                  <ChevronDown size={14} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                sideOffset={6}
                collisionPadding={16}
                onCloseAutoFocus={(e) => e.preventDefault()}
                data-testid="user-menu-content"
                className="rounded-none w-56"
              >
                <DropdownMenuLabel className="text-xs">
                  <div className="font-semibold text-[#0B1D3A]">{user?.name}</div>
                  <div className="text-slate-500 mt-0.5">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="btn-open-profile" onClick={() => setOpenProfile(true)} className="text-slate-700">
                  <UserCircle size={14} className="mr-2" /> My Profile
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="btn-display-settings" onClick={() => setOpenDisplay(true)} className="text-slate-700">
                  <Palette size={14} className="mr-2" /> Pengaturan Tampilan
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="btn-logout"
                  onSelect={(e) => e.preventDefault()}
                  onClick={async () => {
                    // logout() sudah:
                    //   • POST /auth/logout (best-effort — tampilkan warning bila gagal)
                    //   • sendBeacon presence/offline
                    //   • clear auth-only local state (preservasi dashboard/layout)
                    //   • window.location.replace("/login") (Back-nav aman)
                    // Semua error di-handle di dalamnya; wrapper try/catch di
                    // sini cukup untuk skenario ekstrem (mis. throw sinkron).
                    try { await logout(); }
                    catch { try { (await import("sonner")).toast.error("Gagal keluar dari sesi. Coba lagi."); } catch {} }
                  }}
                  className="text-rose-600"
                >
                  <LogOut size={14} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 ens-clip-x">
          <Outlet />
        </main>
        <MessengerFAB />
      </div>

      <ProfileDialog open={openProfile} onOpenChange={setOpenProfile} />
      <DisplaySettingsDialog open={openDisplay} onOpenChange={setOpenDisplay} prefs={displayPrefs} onSaved={setDisplayPrefs} />
      <AnnouncePopup />
      {/* 149-ar: Global gate — user WAJIB ganti password (setelah admin reset atau
          create-user default). Dialog tidak bisa ditutup tanpa selesai. Semua page
          di belakangnya tetap render (via <Outlet />) tapi tidak dapat di-interact. */}
      <ForceChangePasswordDialog open={!!mustChangePassword} />
    </div>
  );
}
